import { VueRenderer } from '@tiptap/vue-2';
import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import store from '@/store';
import MentionList from './MentionList.vue';
import { SuggestionEventProps } from '@/components/tiptap/extensions/Mention/SuggestionEventProps';
import { User } from '@/models/User';

function createFloatingPopup(clientRect: (() => DOMRect | null) | null, element: Element): HTMLElement {
  const popup = document.createElement('div');
  popup.style.position = 'absolute';
  popup.style.zIndex = '9999';
  popup.appendChild(element);
  document.body.appendChild(popup);
  popup.addEventListener('mousedown', (e) => e.preventDefault());
  updateFloatingPopup(popup, clientRect);
  return popup;
}

function updateFloatingPopup(popup: HTMLElement, clientRect: (() => DOMRect | null) | null): void {
  if (!clientRect) return;

  const rect = clientRect();
  if (!rect) return;

  const virtualEl = {
    getBoundingClientRect: () => rect,
  };

  computePosition(virtualEl, popup, {
    placement: 'bottom-start',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
  }).then(({ x, y }) => {
    Object.assign(popup.style, {
      left: `${x}px`,
      top: `${y}px`,
    });
  });
}

export default {
  items: ({ query }: { query: string }) => {
    const companyUsers = store.state.companyUsers;
    const q = query.toLowerCase();

    return companyUsers
      .filter((user) => {
        const name = user.name.toLowerCase();
        const email = user.email.toLowerCase();
        return name.includes(q) || email.includes(q);
      })
      .map((user) => User.parse(JSON.parse(JSON.stringify(user))));
  },

  render: () => {
    let component: VueRenderer;
    let popup: HTMLElement | null = null;

    return {
      onStart: (props: SuggestionEventProps) => {
        component = new VueRenderer(MentionList, {
          parent: this,
          propsData: props,
        });

        if (!props.clientRect) {
          return;
        }

        popup = createFloatingPopup(props.clientRect, component.element);
      },

      onUpdate(props: SuggestionEventProps) {
        component.updateProps(props);

        if (!props.clientRect || !popup) return;

        updateFloatingPopup(popup, props.clientRect);
      },

      onKeyDown(props: SuggestionEventProps) {
        if (props.event.key === 'Escape') {
          if (popup) popup.style.display = 'none';
          return true;
        }

        return (component.ref as MentionList)?.onKeyDown(props);
      },

      onExit() {
        if (popup) {
          popup.remove();
          popup = null;
        }
        component.destroy();
      },
    };
  },
};
